import { Files, GearSix, X } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FilterButton from "common/components/ui/FilterButton";
import FormField from "common/components/ui/FormField";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "common/components/ui/Select";
import useCampaigns from "common/datahooks/useCampaigns";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Rb2bIntegration as Rb2bIntegrationType } from "common/types";

import useIntegrationMutations from "../datahooks/useIntegrationMutations";
import RemoveIntegration from "./RemoveIntegration";
import WebhookUrlInput from "./WebhookUrlInput";

interface Rb2bIntegrationProps {
  settings: Rb2bIntegrationType | null;
}

export default function Rb2bIntegration({ settings }: Rb2bIntegrationProps) {
  const isDesktop = useTwBreakpoint("lg");
  const isConnected = settings !== null;

  const [selectedCampaignId, setSelectedCampaignId] = useState(
    settings?.campaign_id,
  );
  const { campaigns, isLoadingCampaigns } = useCampaigns();
  const { connectRb2b, isConnectingRb2b } = useIntegrationMutations();

  let selectedCampaignLabel = "Select campaign";

  if (selectedCampaignId) {
    const campaign = campaigns?.find(({ id }) => id === selectedCampaignId);

    selectedCampaignLabel = campaign?.name ?? "Deleted campaign";
  }

  const generateButtonComponent = (
    <Button
      variant="outline-black"
      disabled={isConnected || !selectedCampaignId}
      isLoading={isConnectingRb2b}
      onClick={() => connectRb2b(selectedCampaignId)}
    >
      Generate
    </Button>
  );

  const Component = isDesktop ? Popover : Drawer;
  const Trigger = isDesktop ? PopoverTrigger : DrawerTrigger;
  const Content = isDesktop ? PopoverContent : DrawerContent;

  return (
    <Component>
      <Trigger asChild>
        <Button variant="secondary-black" intent="iconOnly" size="sm">
          <GearSix />
        </Button>
      </Trigger>
      <Content className="lg:w-96" align="start">
        {isDesktop ? (
          <div className="p-4 flex items-center border-b border-b-black-200 justify-between">
            <h3 className="text-md-bold">RB2B settings</h3>
            <PopoverClose>
              <X className="text-black-500 size-5" />
            </PopoverClose>
          </div>
        ) : (
          <DrawerTitle className="mb-8">RB2B settings</DrawerTitle>
        )}

        <div className="flex flex-col lg:p-4 gap-6">
          <FormField label="Select campaign" htmlFor="campaign-select">
            <Select
              value={selectedCampaignId}
              onValueChange={setSelectedCampaignId}
              disabled={isConnected}
            >
              <SelectTrigger asChild>
                <FilterButton
                  leftIcon={<Files />}
                  isLoading={isLoadingCampaigns}
                >
                  {selectedCampaignLabel}
                </FilterButton>
              </SelectTrigger>
              <SelectContent className="max-w-[--radix-select-trigger-width]">
                {(campaigns || []).map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {isConnected ? (
            <FormField
              label="Copy this URL and paste it to your RB2B settings"
              htmlFor="webhook-url"
              hint="Generating a new RB2B URL will invalidate any previously generated URLS"
            >
              <div className="flex items-center gap-x-2.5">
                <WebhookUrlInput webhookUrl={settings?.webhook_url} />
                {generateButtonComponent}
              </div>
            </FormField>
          ) : (
            generateButtonComponent
          )}
        </div>

        {isConnected && (
          <div className="flex flex-col gap-y-6 lg:p-4">
            <RemoveIntegration type="rb2b" />
          </div>
        )}
      </Content>
    </Component>
  );
}
